<template>
  <div>
    <div class="panel-heading order-heading-with-stats">
      <h4 class="panel-title">{{ $t('orders.newOrdersTitle') }}</h4>

      <div class="order-heading-right">
        <div class="order-toolbar-buttons">
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="toggleFiltersVisible">
            <i class="material-icons">{{ showFilters ? 'expand_less' : 'expand_more' }}</i>
            <span v-if="showButtonLabels">{{ $t('orders.filtersToggleLabel') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="reloadTable">
            <i class="material-icons">cached</i>
            <span v-if="showButtonLabels">{{ $t('datatable.reloadShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('add_new_order')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openAddNewOrderModal(); checkAlert = false">
            <i class="fa fa-plus" style="color:#006b8d"></i>
            <span v-if="showButtonLabels">{{ $t('datatable.newOrderButton') }}</span>
          </a>
          <a v-if="$_has_perm('add_bulk_order')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openAddMultipleOrdersModal()">
            <i class="fa fa-list-ul" style="color:#00796b"></i>
            <span v-if="showButtonLabels">{{ $t('datatable.bulkOrdersButton') }}</span>
          </a>
          <a v-if="$_has_perm('send_multiple_to_process')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="sendAllToProcess()">
            <i class="material-icons" style="color:#2E7D32">send</i>
            <span v-if="showButtonLabels">{{ $t('datatable.sendAllToProcessButton') }}</span>
          </a>
          <a class="order-toolbar-display-toggle" @click="$_toggle_toolbar_button_display()">
            <i class="material-icons">{{ showButtonLabels ? 'label_off' : 'label' }}</i>
          </a>
        </div>

        <div class="order-stats-row order-stats-row--compact">
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--blue"><i class="material-icons">list_alt</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('reports.totalOrders') }}</span>
              <span class="order-stat-value">{{ summaryTotalOrders }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--green"><i class="material-icons">attach_money</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('printLogs.totalAmountLabel') }}</span>
              <span class="order-stat-value">$ {{ summaryTotalAmount.toFixed(2) }}</span>
              <span v-if="qrCodeValues.length > 0" class="order-stat-note">({{ qrCodeValues.length }} selected)</span>
              <span v-else-if="!filterMade" class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--purple"><i class="material-icons">today</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.ordersTodayLabel') }}</span>
              <span class="order-stat-value">{{ ordersTodayCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--orange"><i class="material-icons">payment</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.pendingPaymentsLabel') }}</span>
              <span class="order-stat-value">{{ pendingPaymentsCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="panel-body"
      style="display: block;"
    >
      <div class="row">
        <preDatatableComponent
          ref="preDatatable"
          :removeAddButton="true"
          :removeWarehouseDeliverButton="true"
          :hideReloadButton="true"
          :collapsibleFilters="true"
          :filtersVisible="showFilters"
          :permissions="{
            add_permission: 'add_new_order',
            add_bulk_permission: 'add_bulk_order',
            send_multiple_to_process: 'send_multiple_to_process'
          }"
          :openAddNewOrderModal="openAddNewOrderModal"
          :openAddMultipleOrdersModal="openAddMultipleOrdersModal"
          :sendAllToProcess="sendAllToProcess"
          :checkAlert="checkAlert"
          :tableData="tableData"
          :resetGetResults="resetGetResults"
          :searchMe="searchMe"
          :enableCustomerSearchSuggestions="true"
          :pagination="pagination"
          :perPageFix="perPageFix"
          :filterTable="filterTable"
          :resetGetResults2="resetGetResults2"
          :columns="columns"
          :showHideColumn="showHideColumn"
          :storageKey="'cols_newOrder_v3'"
          :defaultHideCol="defaultHideCol"
          :resultsVersion="resultsVersion"
          :filterMade="filterMade"
          :totalResult="totalResult"
          :warehouseFilterItems="warehouses"
          :warehouseFilterChange="handleWarehouseFilter"
          @branch-edit="branchEdit"
        >
          <template slot="custom-filter-start">
            <template v-if="showFilters">
            <v-flex class="filter-field px-2">
              <v-select
                :items="orderSortDirItems"
                :value="tableData.sortDir"
                @input="handleOrderSortDirFilter"
                :label="$t('orders.orderSort')"
                hide-details
                single-line
                item-text="text"
                item-value="value"
              ></v-select>
            </v-flex>
            </template>
          </template>
        </preDatatableComponent>

        <div class="col-xs-12">
          <div class="row text-center">
            <span
              id="test-editable"
              style="opacity:0"
            ></span>
          </div>
          <div class="table-responsive oc-table-shell">
          <datatable
            v-if="!isMobile"
            :sortDir="tableData.sortDir"
            :columns="columns"
            :sortByColumn="sortByColumn"
            :searchOneCol="searchOneCol"
            :sortKey="sortKey"
            :sortOrders="sortOrders"
            :checkAll="checkAll"
            :unCheckAll="unCheckAll"
          >
            <tbody>
              <tr
                v-for="(r, index) in apiResults"
                :key="r.order_id"
                :style="qrCodeValues.find(x => x == r.order_id) ? 'background-color: #FFF9C4' : '' "
              >
                <td>
                  <input
                    :id="'cb' + index"
                    type="checkbox"
                    :value="[r.order_id]"
                    v-model="qrCodeValues"
                  />
                </td>
                <td class="oc-cell hover-container">
                  <div class="oc-order-number">#{{ r.theOrderID }}</div>
                  <div class="oc-order-sub" v-if="r.qrcode"><i class="fa fa-qrcode" aria-hidden="true"></i> {{ r.qrcode }}</div>
                  <div class="oc-order-sub"><view-order-users :orderData="r" /></div>
                </td>
                <td class="oc-cell ws-nowrap">
                  <customer-device-icon :orderSource="r.order_source" />
                  <img v-if="r.profile_image" :src="r.profile_image && r.profile_image.startsWith('http') ? r.profile_image : `${$_image_path()}${r.profile_image}`" class="customer-profile-image" />
                  <span class="customer-name-stack">
                    <span v-if="r.full_name && r.full_name.trim().split(/\s+/).length > 3" style="word-break: break-word; white-space: pre-line;">
                      {{ r.full_name.replace(/((?:\S+\s+){3})/g, '$1\n') }}
                    </span>
                    <span v-else>{{ r.full_name }}</span>
                    <span v-if="r.customer_id_fk" class="oc-customer-id">ID: {{ r.customer_id_fk }}</span>
                    <span v-if="r.phone_number" class="customer-phone-line">{{ r.phone_number }}</span>
                    <span v-if="r.phone_number_2" class="customer-phone-line">{{ r.phone_number_2 }}</span>
                  </span>
                  <open-whatsapp-and-viber :phoneNumber="r.phone_number" />
                </td>
                <td class="oc-cell">
                  <div class="oc-item-name">{{
                    r.product_name.substr(0, 35) +
                      (r.product_name.length > 35 ? '...' : '')
                  }}</div>
                  <div class="oc-item-sub" v-if="r.brand">{{ $t('columns.brand') }}: <strong>{{ r.brand }}</strong></div>
                  <a v-if="r.product_url" :href="r.product_url" target="_blank" rel="noopener noreferrer" class="oc-item-link">
                    <i class="material-icons">link</i> {{ $t('columns.url') }}
                  </a>
                </td>
                <td class="oc-cell oc-product-cell">
                  <img
                    v-if="r.image"
                    :src="r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`"
                    class="oc-thumb"
                    @click="lightboxImage = r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`; lightboxOpen = true"
                  />
                  <span v-else class="oc-thumb-placeholder">—</span>
                </td>
                <td class="oc-cell">
                  <span
                    class="oc-status-chip"
                    style="background:#DEC384; color:#6B4C00;"
                  >{{ $_status_label(r.status) }}</span>
                  <div class="oc-location-line" v-if="r.country">
                    <i class="material-icons oc-location-icon">place</i>{{ r.country }}<span v-if="r.transportation_type_obj"> · {{ r.transportation_type_obj.name.replace('Transportation', '') }}</span>
                  </div>
                </td>
                <td class="combined-cell-td oc-cell">
                  <div class="combined-cell">
                    <div><span class="combined-cell-label">{{ $t('columns.price') }}:</span> {{ r.item_price ? '$' + r.item_price : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.shipping') }}:</span> {{ r.internal_shipping_handling ? '$' + r.internal_shipping_handling : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.total') }}:</span> {{ r.total_amount ? '$' + r.total_amount : '—' }}</div>
                    <div class="combined-cell-divider"></div>
                    <div><span class="combined-cell-label">{{ $t('columns.qty') }}:</span> {{ r.quantity || '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.size') }}:</span> {{ r.size || '—' }}</div>
                  </div>
                </td>
                <td class="oc-cell hover-container" style="min-width: 150px">
                  <view-order-dates :orderData="r" stack-time />
                  <span
                    v-if="r.payment_status"
                    class="oc-payment-chip"
                    :style="{ background: paymentStatusColor(r.payment_status, r.invoice_edit_count).bg, color: paymentStatusColor(r.payment_status, r.invoice_edit_count).color }"
                  >{{ paymentStatusLabel(r.payment_status) }}</span>
                </td>
                <td class="oc-cell oc-actions-cell">
                  <div class="oc-actions-grid">
                    <a
                      v-if="$_has_perm('view_order_detail')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.viewDetails')"
                      @click="initDetail('modal_showApp', r.order_id)"
                    >
                      <i class="material-icons" style="color:#01579B">aspect_ratio</i>
                    </a>
                    <span v-if="$_has_perm('process_new_order')" class="oc-action-btn">
                      <process-the-order
                        :processOrder="processOrder"
                        :r="r"
                        :checkAlert="checkAlert"
                        :updateData="updateData"
                        :calculateTotalAmount="calculateTotalAmount"
                        :loading="loading"
                        :getResults="getResults"
                        :clearProcessOrderData="clearProcessOrderData"
                        :theQuantity="theQuantity"
                        :itemTypes="itemTypes"
                        :warehouses="warehouses"
                        :reasons="reasons"
                      ></process-the-order>
                    </span>
                    <a
                      v-if="$_has_perm('cancel_new_order')"
                      class="oc-action-btn"
                      @click="openCancelingOrderModal(r.order_id)"
                      :tooltip="$t('orders.cancelThisOrder')"
                    ><i class="material-icons" style="color:#C62828">cancel</i></a>
                    <a
                      v-if="$_has_perm('send_pending_answer_order')"
                      class="oc-action-btn"
                      @click="sendToPendingAnswer(r.order_id)"
                      :tooltip="$t('orders.sendToPendingAnswerTooltip')"
                    >
                      <i class="fa fa-adn" style="color:#FF9966"></i>
                    </a>
                    <a
                      v-if="$_has_perm('send_to_process')"
                      class="oc-action-btn"
                      @click="sendToProcessDirect(r.order_id)"
                      :tooltip="$t('orders.sendToProcessTooltip')"
                    >
                      <i class="material-icons" style="color:#2E7D32">send</i>
                    </a>
                  </div>
                </td>
              </tr>
              <tr id="no-result-row">
                <td
                  :colspan="columns.length"
                  class="text-center"
                >
                  <v-alert
                    type="error"
                    :value="alertValue"
                  >
                    {{ $t('common.noResultsFound') }}
                  </v-alert>
                </td>
              </tr>
            </tbody>
          </datatable>
          </div>

          <!-- Mobile card layout — mirrors every desktop column, no horizontal scroll -->
          <div v-if="isMobile" class="mobile-orders-container">
            <div
              v-for="(r, index) in apiResults"
              :key="r.order_id"
              class="mobile-order-card"
              :style="qrCodeValues.find(x => x == r.order_id) ? 'border-left-color:#FBC02D; background:#FFF9C4;' : ''"
            >
              <!-- Card header: checkbox + Item Code + Status badge -->
              <div class="moc-header">
                <input
                  :id="'cb' + index"
                  type="checkbox"
                  :value="[r.order_id]"
                  v-model="qrCodeValues"
                />
                <span class="moc-item-code">{{ r.theOrderID }}</span>
                <span
                  class="moc-status-badge"
                  style="background:#DEC384; color:#6B4C00;"
                >{{ $_status_label(r.status) }}</span>
              </div>

              <div class="moc-body">
                <!-- User -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('common.user') }}</span>
                  <span class="moc-value"><view-order-users :orderData="r" /></span>
                </div>

                <!-- Customer -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.customer') }}</span>
                  <span class="moc-value">
                    <img v-if="r.profile_image" :src="r.profile_image && r.profile_image.startsWith('http') ? r.profile_image : `${$_image_path()}${r.profile_image}`" class="customer-profile-image" />
                    <span v-if="r.full_name && r.full_name.trim().split(/\s+/).length > 3" style="word-break:break-word; white-space:pre-line;">
                      {{ r.full_name.replace(/((?:\S+\s+){3})/g, '$1\n') }}
                    </span>
                    <span v-else>{{ r.full_name }}</span>
                    <open-whatsapp-and-viber :phoneNumber="r.phone_number" />
                    <span v-if="r.phone_number" class="customer-phone-line">{{ r.phone_number }}</span>
                    <span v-if="r.phone_number_2" class="customer-phone-line">{{ r.phone_number_2 }}</span>
                  </span>
                </div>

                <!-- Item Name -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.itemName') }}</span>
                  <span class="moc-value">{{ r.product_name.substr(0, 60) + (r.product_name.length > 60 ? '...' : '') }}</span>
                </div>

                <!-- Brand -->
                <div class="moc-row" v-if="r.brand">
                  <span class="moc-label">{{ $t('columns.brand') }}</span>
                  <span class="moc-value">{{ r.brand }}</span>
                </div>

                <!-- URL -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.url') }}</span>
                  <span class="moc-value">
                    <a v-if="r.product_url" :href="r.product_url" target="_blank" rel="noopener noreferrer" style="color:#01579B;">
                      <i class="material-icons" style="font-size:20px; vertical-align:middle;">link</i>
                      {{ r.product_url.replace(/https?:\/\//, '').substr(0, 30) + (r.product_url.length > 30 ? '...' : '') }}
                    </a>
                    <span v-else style="color:#bbb;">—</span>
                  </span>
                </div>

                <!-- Image -->
                <div class="moc-row" v-if="r.image">
                  <span class="moc-label">{{ $t('columns.image') }}</span>
                  <span class="moc-value">
                    <img
                      :src="r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`"
                      style="width:80px; height:80px; object-fit:cover; border-radius:4px; cursor:pointer;"
                      @click="lightboxImage = r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`; lightboxOpen = true"
                    />
                  </span>
                </div>

                <!-- Date & Time -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.dateTime') }}</span>
                  <span class="moc-value"><view-order-dates :orderData="r" /></span>
                </div>

                <!-- Warehouse -->
                <div class="moc-row" v-if="r.country">
                  <span class="moc-label">{{ $t('columns.warehouse') }}</span>
                  <span class="moc-value">{{ r.country }}</span>
                </div>

                <!-- Price | Int. Shipping | Total -->
                <div class="moc-row--3col">
                  <div>
                    <span class="moc-label">{{ $t('columns.price') }}</span>
                    <span class="moc-value">{{ r.item_price ? '$' + r.item_price : '—' }}</span>
                  </div>
                  <div>
                    <span class="moc-label">{{ $t('columns.shipping') }}</span>
                    <span class="moc-value">{{ r.internal_shipping_handling ? '$' + r.internal_shipping_handling : '—' }}</span>
                  </div>
                  <div>
                    <span class="moc-label">{{ $t('columns.total') }}</span>
                    <span class="moc-value">{{ r.total_amount ? '$' + r.total_amount : '—' }}</span>
                  </div>
                </div>

                <!-- Qty | Size -->
                <div class="moc-row--2col">
                  <div>
                    <span class="moc-label">{{ $t('columns.qty') }}</span>
                    <span class="moc-value">{{ r.quantity || '—' }}</span>
                  </div>
                  <div>
                    <span class="moc-label">{{ $t('columns.size') }}</span>
                    <span class="moc-value">{{ r.size || '—' }}</span>
                  </div>
                </div>
              </div>

              <!-- Card footer: action buttons (same permissions as desktop) -->
              <div class="moc-actions">
                <a
                  v-if="$_has_perm('view_order_detail')"
                  style="color:#01579B;"
                  :tooltip="$t('orders.viewDetails')"
                  @click="initDetail('modal_showApp', r.order_id)"
                >
                  <i class="material-icons">aspect_ratio</i>
                </a>
                <span v-if="$_has_perm('process_new_order')">
                  <process-the-order
                    :processOrder="processOrder"
                    :r="r"
                    :checkAlert="checkAlert"
                    :updateData="updateData"
                    :calculateTotalAmount="calculateTotalAmount"
                    :loading="loading"
                    :getResults="getResults"
                    :clearProcessOrderData="clearProcessOrderData"
                    :theQuantity="theQuantity"
                    :itemTypes="itemTypes"
                    :warehouses="warehouses"
                    :reasons="reasons"
                  ></process-the-order>
                </span>
                <a
                  v-if="$_has_perm('cancel_new_order')"
                  @click="openCancelingOrderModal(r.order_id)"
                  style="color:#C62828;"
                  :tooltip="$t('orders.cancelThisOrder')"
                ><i class="material-icons">cancel</i></a>
                <a
                  v-if="$_has_perm('send_pending_answer_order')"
                  @click="sendToPendingAnswer(r.order_id)"
                  style="color:#FF9966;"
                  :tooltip="$t('orders.sendToPendingAnswerTooltip')"
                >
                  <i style="font-size:24px;" class="fa fa-adn"></i>
                </a>
                <a
                  v-if="$_has_perm('send_to_process')"
                  @click="sendToProcessDirect(r.order_id)"
                  style="color:#2E7D32;"
                  :tooltip="$t('orders.sendToProcessTooltip')"
                ><i class="material-icons">send</i></a>
              </div>
            </div>

            <div v-if="alertValue" style="text-align:center; padding:16px;">
              <v-alert type="error" :value="alertValue">{{ $t('common.noResultsFound') }}</v-alert>
            </div>
          </div>

          <add-new-order
            :addNewOrderModal="addNewOrderModal"
            :warehouses="warehouses"
            :closeAddNewOrderModal="closeAddNewOrderModal"
            :checkAlert="checkAlert"
            :checkURL="checkURL"
            :makeNewOrder="makeNewOrder"
            :deliveryTypes="deliveryTypes"
            :makeOrderData="makeOrderData"
            :customers="customers"
            :loading="loading"
            :banks="banks"
          >
          </add-new-order>

          <add-multiple-orders
            :addMultipleOrdersModal="addMultipleOrdersModal"
            :warehouses="warehouses"
            :customers="customers"
            :closeAddMultipleOrdersModal="closeAddMultipleOrdersModal"
            :makeMultipleOrders="makeMultipleOrders"
            :loading="loading"
            :checkAlert="multipleOrdersCheckAlert"
            :banks="banks"
            :kurdistanBankId="kurdistanBankId"
          >
          </add-multiple-orders>

          <!-- Image Lightbox -->
          <v-dialog v-model="lightboxOpen" max-width="600" @input="val => { if (!val) lightboxOpen = false }">
            <v-card style="background:#000; text-align:center; padding:8px;">
              <img
                v-if="lightboxImage"
                :src="lightboxImage"
                style="max-width:100%; max-height:80vh; object-fit:contain; border-radius:4px;"
              />
              <div style="margin-top:8px;">
                <v-btn flat dark @click="lightboxOpen = false">{{ $t('common.close') }}</v-btn>
              </div>
            </v-card>
          </v-dialog>

          <view-order-details
            :detialData="detialData"
            :closeModal="closeModal"
          ></view-order-details>

          <admin-cancel-order
            :closeCancelingOrderModal="closeCancelingOrderModal"
            :cancelOrderModal="cancelOrderModal"
            :cancelAlert="cancelAlert"
            :cancelData="cancelData"
            :cancelingOrder="cancelingOrder"
            :reasons="reasons"
          ></admin-cancel-order>

          <div class="row">
            <div class="col-xs-12 text-center oc-pagination">
              <paginate
                :page-count="pagination.lastPage"
                :page-range="2"
                :changePage="changePage"
                :container-class="'pagination'"
                :pagination="pagination"
                :page-class="'page-item'"
              >
              </paginate>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationVue from './Pagination.vue'
import preDatatableComponent from './preDatatableComponent.vue'
import DatatableVue from './Datatable.vue'
import viewOrderDetails from './viewOrderDetails.vue'
import axios from 'axios'
import swal from 'sweetalert'
import addNewOrder from './addNewOrder.vue'
import addMultipleOrders from './addMultipleOrders.vue'
import processTheOrder from './processTheOrder.vue'
import adminCancelOrder from './adminCancelOrder'
import statusHelpers from '../mixins/statusHelpers'
import toolbarButtonDisplay from '../mixins/toolbarButtonDisplay'

export default {
  mixins: [statusHelpers, toolbarButtonDisplay],
  components: {
    preDatatableComponent: preDatatableComponent,
    datatable: DatatableVue,
    paginate: PaginationVue,
    'view-order-details': viewOrderDetails,
    'add-new-order': addNewOrder,
    'add-multiple-orders': addMultipleOrders,
    'process-the-order': processTheOrder,
    'admin-cancel-order': adminCancelOrder
  },
  created() {
    if (this.queryParameter === true) {
      if (
        window.location.search.replace('?order=', '') !== null ||
        window.location.search.replace('?order=', '') !== ''
      ) {
        let value = window.location.search.replace('?order=', '')
        this.searchOneCol('order_id', value)
      }
    }
  },
  mounted() {
    this.$_fetch_statuses()
    this.perPageFix()
    this.getCustomer()
    this.getItemType()
    this.getWarehouses()
    this.getReasons()
    this.getBanks()

    if (window.innerWidth <= 600) {
      this.isMobile = true
    } else {
      this.isMobile = false
    }
  },
  $_veeValidate: {
    validator: 'new',
  },
  watch: {
    $route() {
      if (this.queryParameter === true) {
        if (
          window.location.search.replace('?order=', '') !== null ||
          window.location.search.replace('?order=', '') !== ''
        ) {
          let value = window.location.search.replace('?order=', '')
          this.searchOneCol('order_id', value)
        }
      }
    },
  },
  data() {
    let sortOrders = {}
    // Grouped columns (redesign, mirrors allOrder.vue's pattern) -- was a flat 16-column
    // list; each grouped cell below stacks several related fields. Per-column hide now
    // only makes sense at the group level, so defaultHideCol is emptied and storageKey
    // bumped (v2 -> v3) so stale hidden-column selections don't misapply to new positions.
    let columns = [
      { label: '', name: '', text: this.$t('columns.checkBoxColumn'), value: '1' },
      {
        label: this.$t('columns.orderInfo'),
        text: this.$t('columns.orderInfo'),
        value: '2',
        sortable: true,
        searchable: true,
        name: 'theOrderID',
      },
      {
        label: this.$t('columns.customer'),
        text: this.$t('columns.customer'),
        value: '3',
        sortable: true,
        searchable: true,
        name: 'full_name',
      },
      {
        label: this.$t('columns.itemDetails'),
        text: this.$t('columns.itemDetails'),
        value: '4',
        sortable: true,
        searchable: true,
        name: 'product_name',
      },
      {
        label: this.$t('columns.product'),
        text: this.$t('columns.product'),
        value: '5',
        sortable: false,
        searchable: false,
        name: 'image',
      },
      {
        label: this.$t('columns.statusLocation'),
        text: this.$t('columns.statusLocation'),
        value: '6',
        sortable: true,
        searchable: true,
        name: 'status',
      },
      {
        label: this.$t('columns.pricing'),
        text: this.$t('columns.pricing'),
        value: '7',
        sortable: false,
        searchable: false,
        name: 'price_all',
      },
      {
        label: this.$t('columns.datePayment'),
        text: this.$t('columns.datePayment'),
        value: '8',
        sortable: true,
        searchable: true,
        name: 'order_date',
      },
      {
        label: this.$t('columns.actions'),
        text: this.$t('columns.actions'),
        value: '9',
        sortable: false,
        searchable: false,
        name: 'actions',
      }
    ]
    columns.forEach((column) => {
      sortOrders[column.name] = -1
    })

    return {
      isMobile: false,
      // Grouped cells can't be hidden field-by-field any more -- same tradeoff allOrder.vue made.
      defaultHideCol: [],
      // Hidden by default (per-page preference, same localStorage convention as allOrder.vue).
      showFilters: localStorage.getItem('filtersVisible_newOrder') === 'true',
      loader: null,
      loading: false,
      dialog: false,
      processOrderUrl: 'api/orders',
      makeNewOrderUrl: 'api/orders',
      url: 'api/new_order',
      orderDetailUrl: 'api/show_order_detail',
      getCustomerUrl: 'api/get-customer-to-select',
      getItemTypeUrl: 'api/get-item-type',
      cancelingOrderUrl: 'api/canceling_order_by_admin',
      sendToProcessUrl: 'api/send_to_process',
      sendToProcessMultipleUrl: 'api/send_to_process_multiple',
      qrCodeValues: [],
      rowIndex: '',
      filterMade: false,
      totalResult: 0,
      resultsVersion: 0,
      queryParameter: true,
      alertValue: false,
      checkAlert: false,
      checkURL: false,
      addNewOrderModal: false,
      addMultipleOrdersModal: false,
      multipleOrdersCheckAlert: false,
      lightboxImage: null,
      lightboxOpen: false,
      warehouses: [],
      reasons: [],
      banks: [],
      kurdistanBankId: null,
      orderSortDirItems: [
        { text: this.$t('orders.newestFirst'), value: 'desc' },
        { text: this.$t('orders.oldestFirst'), value: 'asc' }
      ],
      deliveryTypes: [
        { text: this.$t('orders.homeDelivery'), value: 'home delivery' },
        { text: this.$t('orders.officeDelivery'), value: 'office delivery' },
      ],
      customers: [],
      itemTypeResult: [],
      itemTypes: [],
      makeOrderData: {
        customer_id_fk: null,
        warehouse_id_fk: null,
        transportation_type: null,
        product_name: null,
        brand: null,
        product_url: null,
        quantity: 1,
        delivery_type: null,
        coupon: '',
        size: '',
        type: '',
        color: '',
        note: '',
        is_repackage: false,
        is_hold: false,
        send_to_purchase: false,
        bank_id: null,
        other_service: '',
        item_price: '',
        the_tl_price: '',
        the_aed_price: '',
        the_cny_price: '',
        the_gbp_price: '',
        the_eur_price: '',
        internal_shipping_handling: 0,
        image: null,
        fileName: '',
        image_url: ''
      },
      temp_shipping: '',
      cancelData: {
        cancel_reason: ''
      },
      theCancelOrderID: '',
      cancelOrderModal: false,
      cancelAlert: false,
      updateData: {
        item_price: null,
        the_tl_price: null,
        the_aed_price: null,
        the_cny_price: null,
        the_gbp_price: null,
        the_eur_price: null,
        tax: null,
        national_tax: null,
        item_weight: null,
        internal_shipping_handling: null,
        delivery_price: null,
        kreenex_fee: null,
        quantity_charge: null,
        insurance: null,
        total_amount: null,
        from_date: null,
        to_date: null,
        description: '',
        tmp_shipping: null,
        item_weight_lb: null,
        price_weight: null,
        send_sms: false,
        discount: null,
        item_type: '',
        transportation_type: null,
        is_repackage: false,
        is_hold: false,
        other_service: '',
        local_delivery: 0,
        process_note: '',
        customer_process_note: '',
        repackage_price: null,
        hold_price: null,
        other_service_price: null,
        // Phase 6: new order-info fields
        customer_id_fk: null,
        product_name: null,
        product_url: null,
        quantity: null,
        color: null,
        size: null,
        note: null,
        brand: null
      },
      detialData: {},
      btnsave: true,
      alreadyAnimated: false,
      prevSearchQuery: '',
      apiResults: [],
      detailResults: [],
      customersResult: [],
      theQuantity: {
        quantity: '',
      },
      columns: columns,
      sortKey: 'order_id',
      sortOrders: sortOrders,
      tableData: {
        per_page: 10,
        search: '',
        column: 'order_id',
        sortDir: localStorage.getItem('sortDir_newOrder') || 'desc',
        searchOneCol: '',
        searchOneVal: '',
        from_date: '',
        to_date: '',
        branches_id: '',
        warehouse_id_fk: ''
      },
      pagination: {
        lastPage: 1,
        currentPage: 1,
        total: 1,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: '',
      },
    }
  },

  computed: {
    summaryTotalOrders() {
      if (this.qrCodeValues.length > 0) return this.qrCodeValues.length
      return this.filterMade
        ? this.apiResults.length
        : (this.pagination.total || this.apiResults.length)
    },
    summaryTotalAmount() {
      return this.apiResults.reduce((sum, r) => sum + (Number(r.total_amount) || 0), 0)
    },
    // Best-effort only: counts within the currently loaded page of apiResults, not a true
    // system-wide count -- hence the "(current page)" note shown alongside it in the stat card.
    ordersTodayCount() {
      const today = new Date().toISOString().split('T')[0]
      return this.apiResults.filter(r => {
        const d = r.order_date || r.created_at
        return d && d.substr(0, 10) === today
      }).length
    },
    // Same current-page-only caveat as ordersTodayCount above.
    pendingPaymentsCount() {
      return this.apiResults.filter(r => r.payment_status && r.payment_status !== 'paid').length
    }
  },

  methods: {
    toggleFiltersVisible() {
      this.showFilters = !this.showFilters
      localStorage.setItem('filtersVisible_newOrder', this.showFilters ? 'true' : 'false')
    },

    // Replicates preDatatableComponent.vue's own built-in Reload button (now relocated
    // into the panel-heading here) -- reaches into the child's own filter-chip/date-range
    // reset state via $refs since that state lives inside the shared component, not here.
    reloadTable() {
      this.resetGetResults()
      const preDatatable = this.$refs.preDatatable
      if (preDatatable) {
        preDatatable.filterApplied = false
        preDatatable.resetDatePickerDefaults()
        preDatatable.ware = 'Select'
      }
    },

    paymentStatusColor(status, editCount) {
      if (status === 'not_paid' && !Number(editCount)) {
        return { bg: '#E0E0E0', color: '#616161' }
      }
      const map = {
        not_paid: { bg: '#F63049', color: '#FFFFFF' },
        partial_paid: { bg: '#FFC85C', color: '#6B4C00' },
        paid: { bg: '#89D7B7', color: '#1A5940' },
        cancel: { bg: '#FB8C00', color: '#FFFFFF' }
      }
      return map[status] || { bg: '#F5F5F5', color: '#616161' }
    },

    paymentStatusLabel(status) {
      if (!status) return ''
      return status.replace(/_/g, ' ')
    },

    // Laravel validation errors return message as { field: ["msg", ...] }, not a string -
    // interpolating that object directly renders "[object Object]" in the UI.
    extractErrorMessage(error) {
      const message = error.response && error.response.data && error.response.data.message
      // A web server/proxy body limit answers 413 with an HTML page, so there's no JSON message.
      if (!message && error.response && error.response.status === 413) return this.$t('orders.uploadTooLargeError')
      if (!message) return this.$t('orders.errorOccurredSubmittingOrders')
      if (typeof message === 'string') return message
      return Object.values(message).flat().join(' ')
    },

    sendToProcessDirect(orderId) {
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.sendThisOrderToProcess'),
        icon: 'warning',
        buttons: true
      }).then((confirmed) => {
        if (confirmed) {
          let token = localStorage.getItem('access_token')
          axios
            .put(`${this.sendToProcessUrl}/${orderId}`, {}, {
              headers: { Authorization: 'Bearer ' + token }
            })
            .then(() => {
              this.getResults()
              swal({
                title: this.$t('common.done'),
                text: '',
                icon: 'success',
                timer: 1500,
                buttons: false
              })
            })
            .catch((error) => {
              if (error.response) {
                swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
              }
            })
        }
      })
    },

    sendAllToProcess() {
      const ordersId = this.qrCodeValues.map(e => e[0])

      if (ordersId.length === 0) {
        return swal(this.$t('common.error'), this.$t('orders.pleaseSelectOrder'), 'error')
      }

      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.sendAllSelectedToProcess'),
        icon: 'warning',
        buttons: true
      }).then((confirmed) => {
        if (confirmed) {
          let token = localStorage.getItem('access_token')
          axios
            .put(this.sendToProcessMultipleUrl, { orders_id: ordersId }, {
              headers: { Authorization: 'Bearer ' + token }
            })
            .then(() => {
              this.getResults()
              swal({
                title: this.$t('common.done'),
                text: '',
                icon: 'success',
                timer: 1500,
                buttons: false
              })
            })
            .catch((error) => {
              if (error.response) {
                swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
              }
            })
        }
      })
    },

    sendToPendingAnswer(orderId) {
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.sendToPendingAnswerConfirm'),
        icon: 'warning',
        buttons: true
      }).then((confirmed) => {
        if (confirmed) {
          let token = localStorage.getItem('access_token')
          axios
            .put(`api/pending_answer_order/${orderId}`, {}, {
              headers: { Authorization: 'Bearer ' + token }
            })
            .then(() => {
              this.getResults()
              swal({
                title: this.$t('common.done'),
                text: '',
                icon: 'success',
                timer: 1500,
                buttons: false
              })
            })
            .catch((error) => {
              if (error.response) {
                swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
              }
            })
        }
      })
    },

    branchEdit(branches) {
      this.tableData.branches_id = branches.toString()
      this.getResults()
    },

    handleWarehouseFilter(val) {
      this.tableData.warehouse_id_fk = val != null ? val : ''
      this.url = 'api/new_order'
      this.getResults()
    },

    handleOrderSortDirFilter(val) {
      this.tableData.sortDir = val || 'desc'
      this.tableData.column = 'order_id'
      localStorage.setItem('sortDir_newOrder', this.tableData.sortDir)
      this.url = 'api/new_order'
      this.getResults()
    },

    getWarehouses() {
      let token = localStorage.getItem('access_token')
      axios
        .get('api/warehouses', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.warehouses = [{ text: this.$t('orders.warehousesPlaceholder'), value: null }]
          let result = response.data.data
          result.forEach((r) => {
            this.warehouses.push({ text: r.country, value: r.warehouse_id, transportation_types: r.transportation_types, currency: r.currency, estimate_days: r.estimate_days })
          })
          // Auto-select first warehouse
          if (this.warehouses.length > 0 && !this.makeOrderData.warehouse_id_fk) {
            this.makeOrderData.warehouse_id_fk = this.warehouses[0].value
            // Auto-select first transportation type for the selected warehouse
            if (this.warehouses[0].transportation_types && this.warehouses[0].transportation_types.length > 0) {
              this.makeOrderData.transportation_type = this.warehouses[0].transportation_types[0].id
            }
          }
        })
    },

    getReasons() {
      let token = localStorage.getItem('access_token')
      axios
        .get('api/reason/list', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let result = response.data
          this.reasons = result.map(r => ({
            text: r.cancel_reason,
            value: r.cancel_reason,
            ar: r.cancel_reason_ar,
            ku: r.cancel_reason_ku
          }))
        })
    },

    getBanks() {
      let token = localStorage.getItem('access_token')
      axios
        .get('api/get_banks', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let result = response.data.data
          result.forEach((c) => {
            this.banks.push({ text: c.bank_name, value: c.bank_id })
            if (c.bank_code === 'kur') {
              this.kurdistanBankId = c.bank_id
            }
          })
          if (!this.makeOrderData.bank_id) {
            this.makeOrderData.bank_id = this.kurdistanBankId
          }
        })
    },

    showHideColumn(prevColVal, currentColVal) {
      let a = []
      let diff = []

      if (currentColVal.length < prevColVal.length) {
        for (let i = 0; i < prevColVal.length; i++) {
          a[prevColVal[i]] = true
        }

        for (let i = 0; i < currentColVal.length; i++) {
          if (a[currentColVal[i]]) {
            delete a[currentColVal[i]]
          } else {
            a[currentColVal[i]] = true
          }
        }

        for (let k in a) {
          diff.push(k)
        }

        for (let c = 0; c < diff.length; c++) {
          $('#datatable td:nth-child(' + diff[c] + '), #datatable th:nth-child(' + diff[c] + ')').toggle()
        }
      } else {
        for (let i = 0; i < currentColVal.length; i++) {
          let visible = $(
            '#datatable td:nth-child(' + currentColVal[i] + '), #datatable th:nth-child(' + currentColVal[i] + ')'
          ).is(':visible')
          if (visible) {
            $(
              '#datatable td:nth-child(' + currentColVal[i] + '), #datatable th:nth-child(' + currentColVal[i] + ')'
            ).toggle()
          }
        }
      }
    },

    resetGetResults2() {
      this.resetGetResults()
    },

    openCancelingOrderModal(orderId) {
      this.cancelOrderModal = true
      this.theCancelOrderID = orderId
    },

    closeCancelingOrderModal() {
      this.cancelOrderModal = false
      this.cancelAlert = false
      this.resetCancelData()
    },

    resetCancelData() {
      this.cancelData.cancel_reason = ''
      this.cancelData.cancel_reason_ar = ''
      this.cancelData.cancel_reason_ku = ''
    },

    cancelingOrder() {
      let token = localStorage.getItem('access_token')
      axios
        .put(this.cancelingOrderUrl + '/' + this.theCancelOrderID, this.cancelData, {
          headers: { Authorization: 'Bearer ' + token },
        })
        .then((response) => {
          this.getResults()
          this.cancelAlert = true
          let self = this
          setTimeout(() => {
            self.closeCancelingOrderModal()
          }, 1500)
        })
    },

    getResults(url = this.url) {
      var token = localStorage.getItem('access_token')
      let theDatatable = document.querySelector('.the-datatable')
      let thePanel = document.querySelector('.panel-flat')

      // A selection made before a filter/search/reload must not silently carry over into
      // the new result set — otherwise a stale row (no longer even visible) stays selected
      // and gets included in the next bulk action, affecting rows the user never intended
      // to touch in the current (filtered) view.
      this.qrCodeValues = []

      if (theDatatable != null) {
        theDatatable.classList.add('slide-table-out')
        thePanel.style.overflowX = 'auto'
      }

      axios
        .get(url, { params: this.tableData, headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          const isFlatArray = Array.isArray(data.data)
          if (isFlatArray) {
            this.filterMade = true
            this.apiResults = data.data
          } else {
            this.filterMade = false
            this.apiResults = data.data.data
          }
          this.resultsVersion++

          if (this.apiResults.length === 0) {
            this.tableData.searchOneCol = ''
            this.tableData.searchOneVal = ''
            this.tableData.search = ''
            this.alertValue = true
          } else {
            this.alertValue = false
          }
          if (isFlatArray) {
            this.removeConfigPagination()
          } else {
            this.configPagination(data.data)
          }
          if (theDatatable) {
            theDatatable.classList.remove('slide-table-out')
          }
          setTimeout(function () {
            if (thePanel) thePanel.style.overflowX = 'auto'
          }, 0)
        })
        .then(() => {
          this.totalResult = 0
          for (let i = 0; i < this.apiResults.length; i++) {
            this.totalResult += 1
          }
        })
        .catch((errors) => { })
    },

    resetGetResults() {
      this.queryParameter = false
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.getResults()

      let a = this
      setTimeout(function () {
        a.queryParameter = true
      }, 1000)
    },

    perPageFix(perPage) {
      this.tableData.per_page = perPage
      this.getResults()
    },

    makeNewOrder() {
      this.loading = true
      let token = localStorage.getItem('access_token')
      const formData = new FormData()

      this.makeOrderData.delivery_type = 'office delivery'

      // Append form data
      for (const key in this.makeOrderData) {
        // Only append image_url if it's not empty and no file is selected
        if (key === 'image_url' && this.makeOrderData.image_url && !this.makeOrderData.image) {
          formData.append(key, this.makeOrderData[key])
        } else if (key !== 'image_url') {
          formData.append(key, this.makeOrderData[key])
        }
      }

      axios
        .post(this.makeNewOrderUrl, formData, {
          headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' }
        })
        .then(() => {
          this.loading = false
          this.checkURL = false
          this.checkAlert = true
          let a = this
          setTimeout(function () {
            a.clearNewOrerData()
          }, 2000)
          this.getResults()
        })
        .catch((error) => {
          this.loading = false
          this.checkAlert = false
          if (error.response.data.message.product_url) {
            this.checkURL = true
          } else {
            this.checkURL = false
            swal(this.$t('common.error'), this.extractErrorMessage(error), 'error')
          }
        })
    },

    openAddNewOrderModal() {
      this.addNewOrderModal = true
    },

    closeAddNewOrderModal() {
      this.addNewOrderModal = false
      this.clearNewOrerData()
    },

    openAddMultipleOrdersModal() {
      this.addMultipleOrdersModal = true
    },

    closeAddMultipleOrdersModal() {
      this.addMultipleOrdersModal = false
      this.multipleOrdersCheckAlert = false
      this.resetGetResults()
    },

    // Uploads row images in small batches to api/orders/bulk-images, storing each returned server
    // filename on its row (row.uploadedImage). Sending all images with bulk-store in one request
    // broke past ~20-35 rows: PHP silently drops files past max_file_uploads (default 20) and
    // rejects the whole body past post_max_size, so orders were saved without images or not at all.
    uploadBulkOrderImages(rows, onProgress) {
      var token = localStorage.getItem('access_token')
      var MAX_FILES = 5
      var MAX_BYTES = 20 * 1024 * 1024
      var pending = []
      rows.forEach(function(row, index) {
        if (row.image && !row.uploadedImage) pending.push({ row: row, index: index })
      })

      var batches = []
      var current = []
      var currentBytes = 0
      pending.forEach(function(item) {
        var size = item.row.image.size || 0
        if (current.length && (current.length >= MAX_FILES || currentBytes + size > MAX_BYTES)) {
          batches.push(current)
          current = []
          currentBytes = 0
        }
        current.push(item)
        currentBytes += size
      })
      if (current.length) batches.push(current)

      var done = 0
      var report = function() { if (onProgress) onProgress({ done: done, total: pending.length }) }
      report()

      var sendBatch = function(batch, attempt) {
        var formData = new FormData()
        formData.append('image_count', batch.length)
        batch.forEach(function(item) {
          formData.append('images[' + item.index + ']', item.row.image)
        })
        return axios.post('api/orders/bulk-images', formData, {
          headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' }
        }).then(function(response) {
          var names = response.data.data.names
          batch.forEach(function(item) {
            item.row.uploadedImage = names[item.index]
          })
          done += batch.length
          report()
        }).catch(function(error) {
          // Retry once on a dropped connection / server error; validation errors won't fix themselves.
          var status = error.response && error.response.status
          if (attempt < 2 && (!status || status >= 500)) return sendBatch(batch, attempt + 1)
          throw error
        })
      }

      return batches.reduce(function(chain, batch) {
        return chain.then(function() { return sendBatch(batch, 1) })
      }, Promise.resolve())
    },

    makeMultipleOrders(rows, customerId, warehouseId, transportationType, sendToPurchase, purchaseBankId, onProgress) {
      var self = this
      return self.uploadBulkOrderImages(rows, onProgress).then(function() {
        if (onProgress) onProgress(null)
        return self.saveMultipleOrders(rows, customerId, warehouseId, transportationType, sendToPurchase, purchaseBankId)
      }).catch(function(error) {
        swal(self.$t('common.error'), self.extractErrorMessage(error), 'error')
        throw error  // re-throw so submitAllOrders .catch() resets isSubmitting
      })
    },

    saveMultipleOrders(rows, customerId, warehouseId, transportationType, sendToPurchase, purchaseBankId) {
      var token = localStorage.getItem('access_token')

      // Images were already uploaded by uploadBulkOrderImages — each row only carries its filename.
      var formData = new FormData()
      formData.append('warehouse_id_fk', warehouseId)
      formData.append('transportation_type', transportationType)
      formData.append('send_to_purchase', sendToPurchase ? '1' : '0')
      if (sendToPurchase && purchaseBankId) {
        formData.append('bank_id', purchaseBankId)
      }
      if (customerId) {
        formData.append('customer_id_fk', customerId)
      }

      // Serialize non-file fields as a JSON string
      var ordersJson = rows.map(function(row) {
        return {
          product_name:               row.product_name,
          brand:                      row.brand || '',
          product_url:                row.product_url,
          quantity:                   parseInt(row.quantity, 10) || 1,
          color:                      row.color || '',
          size:                       row.size || '',
          note:                       row.note || '',
          item_price:                 row.item_price !== undefined ? row.item_price : (row.price || 0),
          the_tl_price:               row.the_tl_price || 0,
          the_aed_price:              row.the_aed_price || 0,
          the_cny_price:              row.the_cny_price || 0,
          the_gbp_price:              row.the_gbp_price || 0,
          the_eur_price:              row.the_eur_price || 0,
          internal_shipping_handling: row.shipping_cost || 0,
          image:                      row.uploadedImage || null,
        }
      })
      formData.append('orders', JSON.stringify(ordersJson))

      // Single HTTP request — backend inserts all orders in one atomic transaction.
      // getResults is called in closeAddMultipleOrdersModal after the dialog closes.
      return axios.post('api/orders/bulk-store', formData, {
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' }
      })
    },

    getCustomer() {
      let token = localStorage.getItem('access_token')

      axios
        .get(this.getCustomerUrl, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.customersResult = response.data.data
          this.customersResult.forEach((c) => {
            const phone1 = c.phone_number || ''
            const phone2 = c.phone_number_2 || ''
            const phones = [phone1, phone2].filter(Boolean).join(' / ')
            this.customers.push({
              text: phones ? c.full_name + ' - Tel: ' + phones : c.full_name,
              value: c.user_id,
              full_name: c.full_name,
              phone_number: phone1,
              phone_number_2: phone2
            })
          })
        })
    },

    getItemType() {
      let token = localStorage.getItem('access_token')

      axios
        .get(this.getItemTypeUrl, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.itemTypeResult = response.data.data
          this.itemTypeResult.forEach((c) => {
            this.itemTypes.push({
              text: c.type_name,
              value: c.type_name,
              shippingPrice: c.type_int_shipping_price
            })
          })
        })
    },

    calculateTotalAmount() {
      let itemPrice = Number(this.updateData.item_price) || 0
      let shipping = Number(this.updateData.internal_shipping_handling) || 0
      let tax = Number(this.updateData.tax) || 0
      let nationalTax = Number(this.updateData.national_tax) || 0
      let delivery = Number(this.updateData.delivery_price) || 0
      let fee = Number(this.updateData.kreenex_fee) || 0
      let insurance = Number(this.updateData.insurance) || 0
      let qttCharge = Number(this.updateData.quantity_charge) || 0
      let discount = Number(this.updateData.discount) || 0
      const qtt = Number(this.updateData.quantity) || Number(this.theQuantity.quantity) || 0
      let repackagePrice = Number(this.updateData.repackage_price) || 0
      let holdPrice = Number(this.updateData.hold_price) || 0
      let otherServicePrice = Number(this.updateData.other_service_price) || 0
      const _userData = JSON.parse(localStorage.getItem('user_data') || '{}')
      const _exRate = Number(_userData.ex_rate) || 1
      let localDelivery = (Number(this.updateData.local_delivery) || 0) / _exRate

      let total = (itemPrice + shipping) * qtt + tax + nationalTax + delivery + fee + insurance + qttCharge + localDelivery

      if (this.updateData.is_repackage) {
        total += repackagePrice
      }

      if (this.updateData.is_hold) {
        total += holdPrice
      }

      if (otherServicePrice > 0) {
        total += otherServicePrice
      }

      if (discount > 0) {
        total -= discount
      }

      this.updateData.total_amount = total.toFixed(2)
    },

    clearNewOrerData() {
      this.makeOrderData.warehouse_id_fk = null
      this.makeOrderData.transportation_type = null
      this.makeOrderData.product_name = null
      this.makeOrderData.brand = null
      this.makeOrderData.product_url = null
      this.makeOrderData.quantity = 1
      this.makeOrderData.note = ''
      this.makeOrderData.coupon = ''
      this.makeOrderData.size = ''
      this.makeOrderData.type = ''
      this.makeOrderData.color = ''
      this.makeOrderData.c_o_d = null
      this.makeOrderData.is_repackage = false
      this.makeOrderData.is_hold = false
      this.makeOrderData.send_to_purchase = false
      this.makeOrderData.bank_id = this.kurdistanBankId
      this.makeOrderData.other_service = ''
      this.makeOrderData.local_delivery = ''
      this.makeOrderData.item_price = ''
      this.makeOrderData.the_tl_price = ''
      this.makeOrderData.the_aed_price = ''
      this.makeOrderData.the_cny_price = ''
      this.makeOrderData.the_gbp_price = ''
      this.makeOrderData.the_eur_price = ''
      this.makeOrderData.internal_shipping_handling = 0
      this.makeOrderData.image = null
      this.makeOrderData.fileName = ''
      this.makeOrderData.image_url = ''
      this.checkAlert = false
      this.checkURL = false
      this.loading = false
    },

    clearProcessOrderData() {
      this.updateData.item_price = null
      this.updateData.the_tl_price = null
      this.updateData.the_aed_price = null
      this.updateData.the_cny_price = null
      this.updateData.the_gbp_price = null
      this.updateData.the_eur_price = null
      this.updateData.tax = null
      this.updateData.item_weight = null
      this.updateData.internal_shipping_handling = 0
      this.updateData.delivery_price = null
      this.updateData.kreenex_fee = null
      this.updateData.insurance = null
      this.updateData.total_amount = null
      this.updateData.from_date = null
      this.updateData.to_date = null
      this.updateData.quantity_charge = null
      this.updateData.description = ''
      this.updateData.item_type = ''
      this.updateData.item_weight_lb = null
      this.updateData.discount = null
      this.updateData.send_sms = false
      this.updateData.is_repackage = false
      this.updateData.is_hold = false
      this.updateData.other_service = ''
      this.updateData.repackage_price = null
      this.updateData.hold_price = null
      this.updateData.other_service_price = null
      this.updateData.local_delivery = null
      this.updateData.tax = null
      this.updateData.national_tax = null
      this.updateData.process_note = ''
      this.updateData.customer_process_note = ''
      // Phase 6: reset new order-info fields
      this.updateData.customer_id_fk = null
      this.updateData.product_name = null
      this.updateData.product_url = null
      this.updateData.quantity = null
      this.updateData.color = null
      this.updateData.size = null
      this.updateData.note = null
      this.updateData.brand = null
      this.updateData.order_image = null
      this.checkAlert = false
    },

    processOrder(event, id, stime, stime1, stime2) {
      let token = localStorage.getItem('access_token')
      this.loading = true
      this.updateData.send_sms = this.updateData.send_sms.toString()

      const formData = new FormData()

      Object.keys(this.updateData).forEach(key => {
        // Handle file fields separately
        if (key === 'customer_process_note_image' || key === 'order_image') return
        if (this.updateData[key] !== null && this.updateData[key] !== undefined) {
          formData.append(key, this.updateData[key])
        }
      })

      if (this.updateData.customer_process_note_image instanceof File) {
        formData.append('customer_process_note_image', this.updateData.customer_process_note_image)
      }

      if (this.updateData.order_image instanceof File) {
        formData.append('order_image', this.updateData.order_image)
      }

      axios.post(this.processOrderUrl + '/' + id, formData, {
        headers: { Authorization: 'Bearer ' + token }
      })
        .then((response) => {
          this.loading = false
          this.clearProcessOrderData()
          stime.time = ''
          stime1.time = ''
          stime2.time = ''
          this.checkAlert = true
          this.getResults()
          let b = this
          setTimeout(function () {
            b.checkAlert = false
          }, 1500)
        })
        .catch((error) => {
          const rawMsg = error && error.response && error.response.data && error.response.data.message
          const msg = rawMsg && typeof rawMsg === 'object'
            ? Object.values(rawMsg).flat().join('\n')
            : (rawMsg || this.$t('orders.errorOccurredProcessingOrder'))
          swal(this.$t('common.error'), msg, 'error')
          this.loading = false
          this.clearProcessOrderData()
          stime.time = ''
          stime1.time = ''
          stime2.time = ''
        })
    },

    changePage(pageUrl) {
      this.url = pageUrl
      this.getResults()
    },

    modal(modal) {
      $('#' + modal).modal({ backdrop: 'static' })
    },

    closeModal(modal) {
      let theModal = $('#' + modal)
      theModal.modal('toggle')
    },

    initDetail(modal, id) {
      let token = localStorage.getItem('access_token')
      axios
        .get(this.orderDetailUrl + '/' + id, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          this.detailResults = data.data
          this.detialData = this.detailResults[0]
        })
      this.modal(modal)
    },

    searchMe(str) {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      if (str.trim() !== '') {
        this.tableData.search = str
        this.prevSearchQuery = str
        this.getResults()
      } else {
        swal({
          text: this.$t('common.typeToSearch'),
          icon: 'warning',
          dangerMode: true,
        })
        if (this.prevSearchQuery !== '') {
          this.tableData.search = ''
          this.prevSearchQuery = ''
          this.getResults()
        }
      }
    },

    sortByColumn(str) {
      this.tableData.column = str
      this.tableData.sortKey = str
      this.tableData.sortDir = this.tableData.sortDir === 'desc' ? 'asc' : 'desc'
      this.getResults()
    },

    searchOneCol(columnName, str) {
      this.tableData.searchOneCol = columnName
      this.tableData.searchOneVal = str
      this.getResults()
    },

    filterTable(fromDate, toDate) {
      this.tableData.from_date = fromDate
      this.tableData.to_date = toDate
      this.getResults()
    },

    configPagination(data) {
      this.pagination.lastPage = data.last_page
      this.pagination.currentPage = data.current_page
      this.pagination.total = data.total
      this.pagination.lastPageUrl = data.last_page_url
      this.pagination.nextPageUrl = data.next_page_url
      this.pagination.prevPageUrl = data.prev_page_url
      this.pagination.from = data.from
      this.pagination.to = data.to
    },

    removeConfigPagination() {
      this.pagination.lastPage = ''
      this.pagination.currentPage = ''
      this.pagination.total = ''
      this.pagination.lastPageUrl = ''
      this.pagination.nextPageUrl = ''
      this.pagination.prevPageUrl = ''
      this.pagination.from = ''
      this.pagination.to = ''
    },

    checkAll() {
      this.qrCodeValues = this.apiResults.map(r => [r.order_id])
    },

    unCheckAll() {
      this.qrCodeValues = []
    },
  },
}
</script>

<style>
@media screen and (max-width: 600px) {
  th {
    font-weight: bold;
    color: #273246;
    padding-left: 5px !important;
  }

  td {
    padding-left: 5px !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}

.table > thead > tr > th, .table > tbody > tr > th, .table > tfoot > tr > th, .table > thead > tr > td, .table > tbody > tr > td, .table > tfoot > tr > td
{
  padding: 0 5px !important;
}

.panel-heading h4 {
  text-transform: uppercase;
  margin-left: -20px;
  padding-left: 10px;
  font-weight: 500;
}

.icon-enlarge,
.icon-pencil {
  color: navy;
}

.icon-bin2 {
  color: red;
}

.icon-calendar {
  color: green;
}

.btn {
  transition: all 0.5s;
}

.col-xs-12 {
  padding-top: 10px;
  padding-bottom: 10px;
}
.overlay--active:before {
  opacity: 50 !important;
  background: rgba(0, 0, 0, 0.5) !important;
}

.dialog {
  box-shadow: 0 3px 5px -1px rgba(14, 14, 14, 0.212) !important;
  overflow: hidden !important;
  overflow-y: auto !important;
}
.page-stats {
  align-items: center;
  position: relative;
  top: -8px;
  margin-right: 5px;
}

.dialog:not(.dialog--fullscreen) {
  max-height: 95%;
}

@media screen and (max-width: 767px) {
  .form-group {
    margin-top: 0;
    margin-bottom: 0;
  }
}

a:hover {
  color: #03a9f4;
  position: relative;
}
a[tooltip]:hover:after {
  content: attr(tooltip);
  padding: 8px 12px;
  color: #ffffff;
  position: absolute;
  right: 80%;
  bottom: 150%;
  white-space: nowrap;
  z-index: 9999;
  -moz-border-radius: 3px;
  -webkit-border-radius: 3px;
  border-radius: 3px;
  -moz-box-shadow: 0px 0px 2px #484b4e;
  -webkit-box-shadow: 0px 0px 2px #484b4e;
  box-shadow: 0px 0px 2px #484b4e;
  background-color: #484b4e;
}

/* Mobile order cards: see assets/mobile-order-cards.css */

.customer-name-stack {
  display: inline-flex;
  flex-direction: column;
  vertical-align: middle;
}
.customer-phone-line {
  display: block;
  font-size: 14px;
  color: #666;
}
.combined-cell-td {
  vertical-align: middle;
}
.combined-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  font-size: 15px;
  line-height: 1.4;
  white-space: nowrap;
}
.combined-cell-label {
  color: #888;
}
.combined-cell-divider {
  width: 100%;
  border-top: 1px solid #ddd;
  margin: 3px 0;
}
</style>



// WEBPACK FOOTER //
// src/components/newOrder.vue